/**
 * The waveform, the playhead, and the markers a curator drags.
 *
 * **Wavesurfer draws and plays; this component owns every pointer.** Wavesurfer
 * renders inside a shadow root, where React's delegated events do not reliably
 * reach, and its own click-to-seek would fire underneath every marker drag. So
 * Wavesurfer is created with `interact: false` and a transparent overlay above it
 * takes all input: near a marker is a grab, anywhere else is a seek.
 *
 * **Nothing is decoded in the webview.** Peaks come from the engine
 * (nova/analysis/peaks.py) and the audio element streams the file with Range
 * requests, so stepping through fifty tracks does not pile up fifty PCM copies.
 *
 * **One timeline: the file's full length.** A damaged file can claim more time
 * than can be decoded -- the header and the browser say 4:19, the decoder stops
 * at 3:31. Drawing the peaks over one length while markers and playback used the
 * other put every marker out of line with the waveform and capped the Early Out
 * at the decoder's end. So the timeline is the longer of the two, the missing
 * stretch is drawn flat and labelled, and a marker may go anywhere on it.
 *
 * **The beat grid is drawn, never dragged.** Beat lines (and bar lines with their
 * numbers) sit on a canvas under the marker layer, so they never take a pointer
 * from a marker. With `click` on, a short tick is scheduled on the audio clock a
 * moment ahead of each beat -- the "1" higher -- so a grid can be checked by ear.
 *
 * **Position is read per frame, not per `timeupdate`.** The media element reports
 * time about four times a second, which is a quarter-second jitter on a countdown
 * that exists to be read to the millisecond.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { audioUrl, fetchPeaks } from "../api";
import { routeElement } from "../core/audioCache";
import { bus } from "../core/bus";
import type { CueField } from "../types";
import {
  GRAB_PX,
  MARKERS,
  SNAP_PX,
  clampField,
  formatTime,
  nearestMarker,
  timeToX,
  xToTime,
  type CueValues,
  type View,
} from "./cueMath";
import { barLabelStep, beatSeconds, beatsBetween, type Grid } from "./beatMath";

export interface WaveformControls {
  togglePlay: () => void;
  isPlaying: () => boolean;
  currentTime: () => number;
  seek: (t: number) => void;
  /** Play from `t`, optionally stopping at `until`. */
  playFrom: (t: number, until?: number) => void;
  zoomBy: (factor: number) => void;
  zoomFit: () => void;
  /** Scroll so `t` is in view, zooming in if the waveform is at fit. */
  reveal: (t: number) => void;
}

const MAX_PX_PER_SEC = 2000;
const WAVE_HEIGHT = 128;
/** The strip under the waveform where hook brackets live and can be moved as one. */
const HOOK_BAND = 16;
/** Seconds of claimed-but-undecodable audio worth warning about. */
const UNDECODED_TOLERANCE = 0.25;

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

type Drag =
  | { kind: "marker"; field: CueField; moved: boolean; startX: number }
  | { kind: "hook"; offset: number; length: number; moved: boolean; startX: number }
  | { kind: "seek"; moved: boolean; startX: number };

export const WaveformEditor = forwardRef<
  WaveformControls,
  {
    trackId: string;
    duration: number;
    values: CueValues;
    sources: Partial<Record<CueField, "set" | "detected" | null>>;
    selectedField: CueField | null;
    onSelectField: (field: CueField | null) => void;
    /** Live, while dragging. */
    onChange: (field: CueField, t: number) => void;
    /** Re-read colours when the theme changes. */
    themeKey: string;
    onPlayState?: (playing: boolean) => void;
    /** The beat grid to draw, when the song has one. */
    grid?: Grid | null;
    /** Tick on every beat while playing. */
    click?: boolean;
  }
>(function WaveformEditor(
  {
    trackId,
    duration,
    values,
    sources,
    selectedField,
    onSelectField,
    onChange,
    themeKey,
    onPlayState,
    grid = null,
    click = false,
  },
  ref
) {
  const gridCanvas = useRef<HTMLCanvasElement | null>(null);
  const gridRef = useRef(grid);
  gridRef.current = grid;
  const clickRef = useRef(click);
  clickRef.current = click;
  const audioRef = useRef<AudioContext | null>(null);
  /** The media time of the last beat a tick was scheduled for. */
  const tickedRef = useRef<number>(-1);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const stopAtRef = useRef<number | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ start: 0, pxPerSec: 1, width: 1 });
  const [time, setTime] = useState(0);
  const playStateRef = useRef(onPlayState);
  playStateRef.current = onPlayState;
  const [hover, setHover] = useState<CueField | "hook" | null>(null);
  const [realDuration, setRealDuration] = useState(duration);
  const realDurationRef = useRef(duration);
  realDurationRef.current = realDuration;
  // Read when a track loads, without making a duration refresh reload the audio.
  const fileDurationRef = useRef(duration);
  fileDurationRef.current = duration;
  /** Where decodable audio ends, when the file claims more; null when it does not. */
  const [decodedEnd, setDecodedEnd] = useState<number | null>(null);

  // One Wavesurfer per mounted editor; tracks are swapped with load(), which
  // keeps the canvas and the audio element instead of rebuilding both.
  useEffect(() => {
    if (!hostRef.current) return;
    const ws = WaveSurfer.create({
      container: hostRef.current,
      height: WAVE_HEIGHT,
      interact: false,
      hideScrollbar: true,
      autoScroll: true,
      autoCenter: true,
      normalize: false,
      cursorWidth: 1,
      waveColor: cssVar("--wave", "#56606f"),
      progressColor: cssVar("--wave-played", "#8b95a7"),
      cursorColor: cssVar("--fg-0", "#f2f4f8"),
    });
    wsRef.current = ws;
    // The desk's preview: the same device as the library's player, and one sound
    // at a time there. Never On air, which is Radio's own player and output.
    void routeElement(ws.getMediaElement(), "main");
    const subs = [
      ws.on("play", () => {
        bus.emit("audio.claim", { owner: "editor" });
        playStateRef.current?.(true);
      }),
      ws.on("pause", () => playStateRef.current?.(false)),
      ws.on("finish", () => playStateRef.current?.(false)),
    ];
    return () => {
      subs.forEach((off) => off());
      ws.destroy();
      wsRef.current = null;
    };
  }, []);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    ws.setOptions({
      waveColor: cssVar("--wave", "#56606f"),
      progressColor: cssVar("--wave-played", "#8b95a7"),
      cursorColor: cssVar("--fg-0", "#f2f4f8"),
    });
  }, [themeKey]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    const abort = new AbortController();
    setStatus("loading");
    setError(null);
    setDecodedEnd(null);
    setTime(0);
    stopAtRef.current = null;
    ws.pause();
    void fetchPeaks(trackId, abort.signal).then(async (p) => {
      if (abort.signal.aborted) return;
      if (p.error || !p.peaks.length) {
        setStatus("error");
        setError(p.error ?? "no waveform for this file");
        return;
      }
      const timeline = Math.max(p.duration, fileDurationRef.current || 0);
      const short = timeline - p.duration > UNDECODED_TOLERANCE;
      let floats = Float32Array.from(p.peaks, (v) => v / 127);
      if (short) {
        // Pad with silence so the peaks cover the whole timeline at their own
        // rate, instead of being stretched to fit it.
        const padded = new Float32Array(Math.round(timeline * p.perSecond));
        padded.set(floats.subarray(0, Math.min(floats.length, padded.length)));
        floats = padded;
      }
      try {
        await ws.load(audioUrl(trackId), [floats], timeline);
        if (abort.signal.aborted) return;
        ws.zoom(0); // fit
        setRealDuration(timeline);
        setDecodedEnd(short ? p.duration : null);
        setStatus("ready");
      } catch (err) {
        if (abort.signal.aborted) return;
        setStatus("error");
        setError(err instanceof Error ? err.message : String(err));
      }
    });
    return () => abort.abort();
  }, [trackId]);

  // Per-frame read of the view and the playhead. State is only set when a value
  // actually moved, so an idle editor does not re-render sixty times a second.
  useEffect(() => {
    let frame = 0;
    const tick = () => {
      const ws = wsRef.current;
      const host = hostRef.current;
      if (ws && host) {
        // The length the waveform was drawn with, not the media element's own
        // reading, so marker pixels and waveform pixels share one scale.
        const d = realDuration || ws.getDuration() || 1;
        const wrapper = ws.getWrapper();
        const width = ws.getWidth() || host.clientWidth || 1;
        const pxPerSec = Math.max(wrapper.scrollWidth, width) / d;
        const start = ws.getScroll() / pxPerSec;
        setView((v) =>
          Math.abs(v.start - start) > 1e-6 || Math.abs(v.pxPerSec - pxPerSec) > 1e-6 || v.width !== width
            ? { start, pxPerSec, width }
            : v
        );
        const t = ws.getCurrentTime();
        const stopAt = stopAtRef.current;
        if (stopAt != null && t >= stopAt) {
          stopAtRef.current = null;
          ws.pause();
        }
        setTime((prev) => (Math.abs(prev - t) > 0.0005 ? t : prev));
        scheduleTicks(ws, t);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [realDuration]);

  /** Ticks for the beats in the next 150 ms, each scheduled once on the audio clock. */
  function scheduleTicks(ws: WaveSurfer, t: number) {
    const g = gridRef.current;
    if (!clickRef.current || !g || !ws.isPlaying()) {
      tickedRef.current = -1;
      return;
    }
    const ctx = (audioRef.current ??= new AudioContext());
    if (ctx.state === "suspended") void ctx.resume();
    // A seek either way starts the schedule again from here.
    if (tickedRef.current > t + 0.2 || tickedRef.current < t - 0.5) tickedRef.current = t - 0.001;
    const rate = ws.getPlaybackRate() || 1;
    for (const b of beatsBetween(g, Math.max(t, tickedRef.current + 0.001), t + 0.15)) {
      const at = ctx.currentTime + Math.max(0, (b.t - t) / rate);
      const osc = ctx.createOscillator();
      const amp = ctx.createGain();
      osc.frequency.value = b.beat === 0 ? 1760 : 1175;
      amp.gain.setValueAtTime(0.0001, at);
      amp.gain.exponentialRampToValueAtTime(0.35, at + 0.002);
      amp.gain.exponentialRampToValueAtTime(0.0001, at + 0.04);
      osc.connect(amp).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.05);
      tickedRef.current = b.t;
    }
  }

  useEffect(() => () => void audioRef.current?.close(), []);

  // Beat and bar lines, redrawn when the view, the grid or the theme moves.
  useEffect(() => {
    const c = gridCanvas.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(view.width * dpr);
    c.height = Math.round(WAVE_HEIGHT * dpr);
    const g = c.getContext("2d");
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, view.width, WAVE_HEIGHT);
    if (!grid || status !== "ready") return;
    const pxPerBeat = beatSeconds(grid) * view.pxPerSec;
    const pxPerBar = pxPerBeat * grid.beatsPerBar;
    const step = barLabelStep(pxPerBar);
    const end = view.start + view.width / view.pxPerSec;
    const barColour = cssVar("--fg-1", "#c9ced8");
    const beatColour = cssVar("--fg-3", "#6b7280");
    g.font = "600 9.5px system-ui, sans-serif";
    g.textBaseline = "top";
    for (const b of beatsBetween(grid, view.start, end)) {
      const isBar = b.beat === 0;
      // Beats only when they are far enough apart to read; bars always, thinned when crowded.
      if (!isBar && pxPerBeat < 6) continue;
      if (isBar && pxPerBar < 4 && (b.bar - 1) % step !== 0) continue;
      const px = Math.round(timeToX(b.t, view)) + 0.5;
      g.globalAlpha = isBar ? 0.55 : 0.6;
      g.fillStyle = isBar ? barColour : beatColour;
      // A bar line runs top to bottom; a beat is a tick at the top and bottom edges.
      if (isBar) g.fillRect(px, 0, 1, WAVE_HEIGHT);
      else {
        g.fillRect(px, 0, 1, 7);
        g.fillRect(px, WAVE_HEIGHT - 7, 1, 7);
      }
      if (isBar && b.bar > 0 && (b.bar - 1) % step === 0) {
        g.globalAlpha = 0.85;
        g.fillText(String(b.bar), px + 3, 2);
      }
    }
    g.globalAlpha = 1;
  }, [grid, view, status, themeKey]);

  const zoomTo = useCallback((pxPerSec: number, anchorX?: number) => {
    const ws = wsRef.current;
    const host = hostRef.current;
    if (!ws || !host) return;
    const d = realDurationRef.current || ws.getDuration() || 1;
    const width = ws.getWidth() || host.clientWidth;
    const fit = width / d;
    const next = Math.min(MAX_PX_PER_SEC, Math.max(fit, pxPerSec));
    const wrapper = ws.getWrapper();
    const current = Math.max(wrapper.scrollWidth, width) / d;
    const x = anchorX ?? width / 2;
    const anchorTime = (ws.getScroll() + x) / current;
    ws.zoom(next <= fit * 1.001 ? 0 : next);
    ws.setScroll(Math.max(0, anchorTime * next - x));
  }, []);

  useImperativeHandle(
    ref,
    (): WaveformControls => ({
      togglePlay: () => {
        const ws = wsRef.current;
        if (!ws || status !== "ready") return;
        stopAtRef.current = null;
        void ws.playPause();
      },
      isPlaying: () => wsRef.current?.isPlaying() ?? false,
      currentTime: () => wsRef.current?.getCurrentTime() ?? 0,
      seek: (t) => wsRef.current?.setTime(Math.max(0, t)),
      playFrom: (t, until) => {
        const ws = wsRef.current;
        if (!ws || status !== "ready") return;
        ws.setTime(Math.max(0, t));
        stopAtRef.current = until ?? null;
        void ws.play();
      },
      zoomBy: (factor) => zoomTo(view.pxPerSec * factor),
      zoomFit: () => zoomTo(0),
      reveal: (t) => {
        const ws = wsRef.current;
        if (!ws) return;
        const fit = view.width / (realDurationRef.current || ws.getDuration() || 1);
        const px = view.pxPerSec <= fit * 1.001 ? Math.max(200, fit) : view.pxPerSec;
        if (px !== view.pxPerSec) ws.zoom(px);
        ws.setScroll(Math.max(0, t * px - view.width / 2));
      },
    }),
    [status, view, zoomTo]
  );

  const localX = (e: { clientX: number }) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    return rect ? e.clientX - rect.left : 0;
  };
  const localY = (e: { clientY: number }) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    return rect ? e.clientY - rect.top : 0;
  };

  const inHookBand = (x: number, y: number) => {
    const { hookStart, hookEnd } = valuesRef.current;
    if (hookStart == null || hookEnd == null) return false;
    return (
      y >= WAVE_HEIGHT - HOOK_BAND &&
      x > timeToX(hookStart, view) + GRAB_PX &&
      x < timeToX(hookEnd, view) - GRAB_PX
    );
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (status !== "ready" || e.button !== 0) return;
    const x = localX(e);
    const y = localY(e);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const field = nearestMarker(x, valuesRef.current, view);
    if (field) {
      dragRef.current = { kind: "marker", field, moved: false, startX: x };
      onSelectField(field);
      return;
    }
    if (inHookBand(x, y)) {
      const { hookStart, hookEnd } = valuesRef.current;
      dragRef.current = {
        kind: "hook",
        offset: xToTime(x, view) - (hookStart as number),
        length: (hookEnd as number) - (hookStart as number),
        moved: false,
        startX: x,
      };
      return;
    }
    dragRef.current = { kind: "seek", moved: false, startX: x };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const x = localX(e);
    const drag = dragRef.current;
    if (!drag) {
      const field = nearestMarker(x, valuesRef.current, view);
      setHover(field ?? (inHookBand(x, localY(e)) ? "hook" : null));
      return;
    }
    if (!drag.moved && Math.abs(x - drag.startX) < 3) return;
    drag.moved = true;
    const d = realDuration || 1;
    // Shift is a fine adjustment: the pointer moves a tenth as far in time.
    const effectiveX = e.shiftKey ? drag.startX + (x - drag.startX) / 10 : x;
    const t = xToTime(effectiveX, view);
    if (drag.kind === "marker") {
      onChange(drag.field, clampField(drag.field, t, valuesRef.current, d));
    } else if (drag.kind === "hook") {
      const start = Math.min(Math.max(0, t - drag.offset), d - drag.length);
      onChange("hookStart", start);
      onChange("hookEnd", start + drag.length);
    } else {
      wsRef.current?.setTime(Math.min(d, Math.max(0, t)));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    const x = localX(e);
    if (drag.kind === "marker" && drag.moved) {
      // A marker let go right by the playhead lands exactly on it: the curator
      // stopped playback on the downbeat, then dragged the marker to it.
      const playheadX = timeToX(time, view);
      if (Math.abs(x - playheadX) <= SNAP_PX) {
        onChange(drag.field, clampField(drag.field, time, valuesRef.current, realDuration));
      }
    } else if (drag.kind === "seek" && !drag.moved) {
      onSelectField(null);
      wsRef.current?.setTime(Math.min(realDuration, Math.max(0, xToTime(x, view))));
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    const ws = wsRef.current;
    if (!ws || status !== "ready") return;
    if (e.ctrlKey || e.metaKey || e.altKey) {
      zoomTo(view.pxPerSec * (e.deltaY < 0 ? 1.25 : 0.8), localX(e));
    } else {
      ws.setScroll(Math.max(0, ws.getScroll() + (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY)));
    }
  };

  const x = (t: number | null) => (t == null ? null : timeToX(t, view));
  const clip = (px: number) => Math.min(view.width, Math.max(0, px));
  const cueInX = x(values.cueIn);
  const introX = x(values.intro);
  const cueOutX = x(values.cueOut);
  const hookStartX = x(values.hookStart);
  const hookEndX = x(values.hookEnd);

  // Countdowns, read against what will air: nothing before the Cue In plays.
  const airStart = values.cueIn ?? 0;
  const from = Math.max(time, airStart);
  const introLeft = values.intro != null ? values.intro - from : null;
  const outLeft = values.cueOut != null ? values.cueOut - from : null;

  const cursor =
    dragRef.current?.kind === "marker" || hover ? "ew-resize" : status === "ready" ? "text" : "default";

  return (
    <div className="wave">
      <div className="wave-readout">
        <span className="wave-clock" title="Playhead">
          {formatTime(time)}
          <em> / {formatTime(realDuration, false)}</em>
        </span>
        <span
          className={`wave-count intro ${introLeft != null && introLeft > 0 ? "live" : "done"}`}
          title="Time left before the vocal comes in"
        >
          <b>INTRO</b>
          {introLeft == null ? "not set" : introLeft > 0 ? formatTime(introLeft) : "VOCAL IN"}
        </span>
        <span
          className={`wave-count out ${outLeft != null && outLeft <= 10 && outLeft > 0 ? "live" : ""}`}
          title="Time left before the mix out, where the next song comes in"
        >
          <b>OUT</b>
          {outLeft == null ? "not set" : outLeft > 0 ? formatTime(outLeft) : "past"}
        </span>
        {decodedEnd != null && (
          <span
            className="wave-damaged"
            title="The file claims to be longer than its audio. Past this point nothing can be decoded: players will stop or play silence. Set the mix out before it."
          >
            ⚠ Audio ends at {formatTime(decodedEnd)} — last {formatTime(realDuration - decodedEnd, false)} undecodable
          </span>
        )}
        <span className="wave-zoom" title="Pixels per second. Ctrl+wheel to zoom, wheel to scroll.">
          {Math.round(view.pxPerSec)} px/s
        </span>
      </div>

      <div className="wave-stage" style={{ height: WAVE_HEIGHT }}>
        <div ref={hostRef} className="wave-host" />
        <canvas ref={gridCanvas} className="wave-grid" style={{ width: view.width, height: WAVE_HEIGHT }} aria-hidden />

        <div
          ref={overlayRef}
          className="wave-overlay"
          style={{ cursor }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => (dragRef.current = null)}
          onPointerLeave={() => !dragRef.current && setHover(null)}
          onWheel={onWheel}
          role="application"
          aria-label="Waveform. Drag markers to move them; click to seek."
        >
          {status === "ready" && (
            <>
              {/* Not aired: before the Cue In and after the Early Out. */}
              {cueInX != null && cueInX > 0 && (
                <div className="zone zone-dead" style={{ left: 0, width: clip(cueInX) }} />
              )}
              {cueOutX != null && cueOutX < view.width && (
                <div
                  className="zone zone-tail"
                  style={{ left: clip(cueOutX), width: view.width - clip(cueOutX) }}
                />
              )}
              {decodedEnd != null && x(decodedEnd)! < view.width && (
                <div
                  className="zone zone-undecoded"
                  style={{ left: clip(x(decodedEnd)!), width: view.width - clip(x(decodedEnd)!) }}
                >
                  <span>no decodable audio</span>
                </div>
              )}
              {/* The talk-over window: from what airs first to the vocal. */}
              {introX != null && (
                <div
                  className="zone zone-intro"
                  style={{
                    left: clip(cueInX ?? x(0)!),
                    width: Math.max(0, clip(introX) - clip(cueInX ?? x(0)!)),
                  }}
                />
              )}
              {hookStartX != null && hookEndX != null && (
                <div
                  className={`zone zone-hook ${hover === "hook" ? "hover" : ""}`}
                  style={{
                    left: clip(hookStartX),
                    width: Math.max(0, clip(hookEndX) - clip(hookStartX)),
                  }}
                />
              )}

              {MARKERS.map(({ field, flag, color, label, key }) => {
                const px = x(values[field]);
                if (px == null || px < -GRAB_PX || px > view.width + GRAB_PX) return null;
                const isHook = field === "hookStart" || field === "hookEnd";
                const detected = sources[field] === "detected";
                return (
                  <div
                    key={field}
                    className={`marker ${isHook ? "hook" : ""} ${field} ${
                      selectedField === field ? "selected" : ""
                    } ${hover === field ? "hover" : ""} ${detected ? "detected" : ""}`}
                    style={{ left: px, ["--marker" as string]: color }}
                    title={`${label} · ${formatTime(values[field])}${
                      detected ? " · detected by Nova" : ""
                    } · key ${key}`}
                  >
                    <span className="marker-grab" style={{ width: GRAB_PX * 2, marginLeft: -GRAB_PX }} />
                    <span className="marker-line" />
                    <span className={`marker-flag ${field === "hookEnd" || field === "cueOut" ? "left" : ""}`}>
                      {flag}
                    </span>
                  </div>
                );
              })}
            </>
          )}
          {status === "loading" && <div className="wave-note">Reading waveform…</div>}
          {status === "error" && <div className="wave-note bad">{error}</div>}
        </div>
      </div>
    </div>
  );
});
